import { DOCUMENT } from '@angular/common';
import { Component, Inject, OnDestroy, OnInit } from '@angular/core';
import { ConfirmationService, MessageService } from 'primeng/api';
import { DialogService } from 'primeng/dynamicdialog';
import { iif, Observable, of, pipe, Subject, throwError, UnaryFunction } from 'rxjs';
import { concatMap, map, takeUntil, tap } from 'rxjs/operators';
import { ModelStatus } from '../../../../shared/dtos/model-status';
import { ProgressData } from '../../../../shared/dtos/progress-data';
import { ResponseDto } from '../../../../shared/dtos/response.dto';
import { SelectionItem } from '../../../../shared/dtos/selection-item';
import { ResponseModel } from '../../../../shared/models/reponse.model';
import { ProgressIndicatingService, progressTap } from '../../../../shared/services/progress-indicating.service';
import { ConfigurationOptions } from '../../dtos/confiuration-options.dto';
import { FrequencyLimitUnitsForResend, FrequencyLimitUnitsWithoutPlural } from '../../dtos/frequency-limit-unit.enum';
import { TopconConfigurationResponseDto } from '../../dtos/partner-configuration.dto';
import { PartnerCUDto } from '../../dtos/partner-cu.dto';
import { TestActivationConfig } from '../../dtos/test-activation-config.dto';
import { ConfigurationOptionsResponseModel } from '../../models/configuration-options-response.model';
import { PartnerModel } from '../../models/partner.model';
import { ServiceLevelConfigurationGroup } from '../../models/service-level-configuration-group.model';
import { SavedServiceLevelConfigurationModel } from '../../models/service-level-configuration.model';
import { TopconConfigurationResponseModel } from '../../models/topcon-configuration-response.model';
import { ServiceLevelConfiguratinoGroupingService } from '../../services/service-level-configuration-grouping.service';
import { TopconConfigurationService } from '../../services/topcon-configuration.service';
import { ServiceLevelConfigDialogComponent, ServiceLevelConfigDialogData } from '../service-level-config-dialog/service-level-config-dialog.component';
import { UnsavedServiceLevelConfigDialogComponent, UnsavedServiceLevelConfigDialogData } from '../service-level-config-dialog/unsaved-service-level-config-dialog.component';
import { TestActivationConfigDialogComponent } from '../test-activation-config-dialog/test-activation-config-dialog.component';
import { UpdatePasswordComponent } from '../update-password/update-password.component';

@Component({
    selector: 'app-topcon-configuration',
    templateUrl: './topcon-configuration.component.html',
    styleUrls: ['./topcon-configuration.component.scss']
})
export class TopconConfigurationComponent implements OnInit, OnDestroy {
    private configurationOptions: ConfigurationOptions;
    private progress$ = new Subject<ProgressData>();
    private unsubscribe$ = new Subject<void>();
    private UNKNOWN_ERROR = 'Unknown error. Please try again.';
    private originalPartner: PartnerCUDto;
    public checkValue : boolean = false;
    public originalData : any;

    public constructor(
        private messageService: MessageService,
        private dialogService: DialogService,
        private confirmationService: ConfirmationService,
        private topconConfigurationService: TopconConfigurationService,
        private serviceLevelConfigurationGroupService: ServiceLevelConfiguratinoGroupingService,
        private progressIndicatingService: ProgressIndicatingService,
        @Inject(DOCUMENT) private document: Document
    ) { }

    public loadingError: string = '';
    public selectedPartner: PartnerModel = PartnerModel.getDefault();
    public selectedPartnerId: string = '';
    public partners: PartnerModel[] = [];
    public partnerIds: string[] = [];
    public configurationGroups: ServiceLevelConfigurationGroup[] = [];
    public testActivationConfigs: TestActivationConfig[] = [];
    public serviceOfferings: SavedServiceLevelConfigurationModel[] = [];
    public frequencyLimitUnits = Object.values(FrequencyLimitUnitsWithoutPlural);
    public frequencyLimitUnitsForResend = Object.values(FrequencyLimitUnitsForResend);
    public accessCodes: SelectionItem[] = [];
    public ngOnDestroy(): void {
        this.progress$.next();
        this.progress$.complete();
        this.unsubscribe$.next();
        this.unsubscribe$.complete();
    }

    public ngOnInit(): void {
        this.progressIndicatingService.show({
            progress$: this.progress$.asObservable(),
            initial: {
                header: 'Loading Topcon Configuration Details',
                details: 'Please wait...',
                done: false
            }
        });
        this.topconConfigurationService
            .getTopconPartners()
            .pipe(
                map(response => response.partnerCUModel.map(p => new PartnerModel(p))),
                tap((partners: PartnerModel[]) => {
                    partners.forEach(partner => {
                        this.partners.push(partner);
                        this.partnerIds.push(partner.partnerId);
                    });
                }),
                progressTap(this.progress$, {
                    header: 'Loading Topcon Configuration Details',
                    details: 'Please wait...',
                    done: false
                }),
                this.reloadConfigurationOptionsMap(),
                takeUntil(this.unsubscribe$)
            ).subscribe(
                () => {
                    this.sendProgressDoneSignal();
                },
                (error: {error: ResponseDto}) => this.handleLoadingError(error));
    }

    private reloadPartnerMap<T>(): UnaryFunction<Observable<T>, Observable<TopconConfigurationResponseDto>> {
        return pipe(
            progressTap(this.progress$, {
                header: 'Loading Topcon Partner\'s Configurations',
                details: 'Please wait...',
                done: false
            }),
            concatMap(() => this.topconConfigurationService.getTopconConfigurationByPartner(this.selectedPartner.organizationId))
        );
    }

    private reloadConfigurationOptionsMap<T>(): UnaryFunction<Observable<T>, Observable<ConfigurationOptionsResponseModel>> {
        return pipe(
            concatMap(() => this.topconConfigurationService.getConfigurationOptions()),
            map(responseDto => new ConfigurationOptionsResponseModel(responseDto)),
            tap((response: ConfigurationOptionsResponseModel) => {
                this.configurationOptions = response.configurationOptions;
                this.accessCodes = this.configurationOptions.accessCodes;
                this.configurationOptions.marketSectors = response.configurationOptions.marketSectors.filter(m => m.name.length > 0);
            })
        );
    }

    private sendProgressDoneSignal(): void {
        this.progress$.next({
            header: '',
            done: true
        });
    }

    public onRefreshClicked(): void {
        this.document.location.reload();
    }

    public showLoadingErrorMessage(): boolean {
        return this.loadingError !== undefined && this.loadingError !== '';
    }

    public onSelectedPartnerIdChanged(): void {
        this.progressIndicatingService.show({
            progress$: this.progress$.asObservable(),
            initial: {
                header: 'Loading Topcon Partner\'s Configuration Data',
                done: false
            }
        });
        const orgId = this.partners.find(p => p.partnerId === this.selectedPartnerId)!.organizationId;
        this.topconConfigurationService
            .getTopconConfigurationByPartner(orgId)
            .pipe(
                progressTap(this.progress$, {
                    header: 'Loading Topcon Partner\'s Configurations',
                    details: 'Please wait...',
                    done: false
                }),
                concatMap(response => iif(() => response.serviceLevelConfiguration === undefined,
                    throwError({ error: { responseMessage: 'Can\'t find configuration for this partner ' + this.selectedPartner.partnerId } }),
                    of(new TopconConfigurationResponseModel(response)))
                ),
                takeUntil(this.unsubscribe$)
            )
            .subscribe(
                (response: TopconConfigurationResponseModel): void => this.handleSelectedPartnerIdChangedResponse(response),
                (error: {error: ResponseDto}) => this.handleLoadingError(error));
        this.configurationGroups = [];
        this.testActivationConfigs = [];
    }

    private handleLoadingError(error: {error: ResponseDto}): void {
        this.sendProgressDoneSignal();
        this.messageService.add({ severity: 'error', summary: 'Fail', detail: 'Unable to load configurations. Please try again.' });
        this.loadingError = error.error?.responseMessage ?? this.UNKNOWN_ERROR;
    }

    private handleSelectedPartnerIdChangedResponse(response: TopconConfigurationResponseModel): void {
        this.sendProgressDoneSignal();
        this.serviceOfferings = response.getServiceLevelConfiguration();
        this.configurationGroups = this.serviceLevelConfigurationGroupService.groupServiceLevelConfigByServiceLevel(this.serviceOfferings);
        this.testActivationConfigs = response.getTestActivationConfigs();
        this.loadingError = '';
        this.originalPartner = response.getPartnerModel().getPartnerDto();
        this.originalPartner.testActivationLimitUnits = this.originalPartner.testActivationLimitUnits ? this.originalPartner.testActivationLimitUnits.substring(0, this.originalPartner.testActivationLimitUnits.length -1) : '';
        this.originalPartner.resendLimitUnits = this.originalPartner.resendLimitUnits ? this.originalPartner.resendLimitUnits.substring(0, this.originalPartner.resendLimitUnits.length - 1) : '';
        this.selectedPartner = response.getPartnerModel();
        this.selectedPartner.maximumTestActivationLimitUnit = this.selectedPartner.maximumTestActivationLimitUnit ? this.selectedPartner.maximumTestActivationLimitUnit.substring(0, this.selectedPartner.maximumTestActivationLimitUnit.length - 1) : '';
        this.selectedPartner.maximumResendLimitUnit = this.selectedPartner.maximumResendLimitUnit ? this.selectedPartner.maximumResendLimitUnit.substring(0, this.selectedPartner.maximumResendLimitUnit.length - 1) : '';
    }

    public disableSaveLimitationsButton(): boolean {
        if (this.selectedPartner.maximumResendLimit > 1000 ||
            this.selectedPartner.maximumResendLimit < 0 ||
            this.selectedPartner.maximumResendLimit === null ||
            this.selectedPartner.maximumTestActivationLimit > 1000 ||
            this.selectedPartner.maximumTestActivationLimit < 0 ||
            this.selectedPartner.maximumTestActivationLimit === null) {
            return true;
        }
        if (this.originalPartner) {
            return this.originalPartner.testActivationLimit === this.selectedPartner.maximumTestActivationLimit &&
                this.originalPartner.testActivationLimitUnits === this.selectedPartner.maximumTestActivationLimitUnit &&
                this.originalPartner.resendLimit === this.selectedPartner.maximumResendLimit &&
                this.originalPartner.resendLimitUnits === this.selectedPartner.maximumResendLimitUnit;
        }
        return true;
    }

    public onUpdatePasswordClicked(): void {
        const ref = this.dialogService.open(UpdatePasswordComponent, {
            modal: true,
            header: 'Reset Password',
            closable: true,
            styleClass: 'password-wrapper',
            width: '43%',
            data: {
                partnerId: this.selectedPartner.partnerId
            }
        });
        ref.onClose.subscribe((saved: boolean) => {
        });
    }

    public onAddNewServiceLevelConfigurationClicked(): void {
        const data: UnsavedServiceLevelConfigDialogData = {
            configurationOptions: this.configurationOptions,
            partner: this.selectedPartner
        };
        const ref = this.dialogService.open(UnsavedServiceLevelConfigDialogComponent, {
            modal: true,
            header: 'Add new Topcon Service Offering',
            closable: true,
            data: data,
            styleClass: 'wrapper'
        });
        ref.onClose.subscribe((saved: boolean) => {
            if (saved) {
                this.onSelectedPartnerIdChanged();
            }
        });
    }

    public onAddTestActivationMappingClicked(): void {
        const ref = this.dialogService.open(TestActivationConfigDialogComponent, {
            modal: true,
            header: 'Add Test Activation Mapping',
            closable: true,
            data: {
                organizationId: this.selectedPartner.organizationId,
                accessCodes: this.configurationOptions.accessCodes
            },
            styleClass: 'wrapper'
        });
        ref.onClose.subscribe((saved: boolean) => {
            if (saved) {
                this.onSelectedPartnerIdChanged();
            }
        });
    }

    public onRowEditClicked(group: ServiceLevelConfigurationGroup, rowIndex? : number): void {
        const clonedConfigs = group.getAllSavedConfigs().map(config => config.clone());
        const temp = new ServiceLevelConfigurationGroup(clonedConfigs);
        const data: ServiceLevelConfigDialogData = {
            serviceLevelConfiguration: temp,
            configurationOptions: this.configurationOptions,
            partner: this.selectedPartner
        };
        const ref = this.dialogService.open(ServiceLevelConfigDialogComponent, {
            modal: true,
            header: 'Edit Topcon Service Offering',
            closable: true,
            data: data,
            styleClass: 'wrapper'
        });
        ref.onClose.subscribe((saved: boolean) => {
            if (saved) {
                this.onSelectedPartnerIdChanged();
            }
        });
    }

    public onUpdatingPartnerSettingClicked(): void {
        this.progressIndicatingService.show({
            progress$: this.progress$.asObservable(),
            initial: {
                header: 'Updating Partner\'s Configuration',
                details: 'Please wait...',
                done: false
            }
        });
        this.topconConfigurationService
            .updatePartnerConfiguration(this.selectedPartner.getPartnerDto())
            .pipe(
                map(response => new ResponseModel(response)),
                takeUntil(this.unsubscribe$)
            )
            .subscribe(
                () => {
                    this.sendProgressDoneSignal();
                    this.originalPartner = this.selectedPartner.getPartnerDto();
                    this.messageService.add({ severity: 'success', summary: 'Success', detail: 'Successfully updated partner\'s configurations.' });
                },
                response => {
                    this.sendProgressDoneSignal();
                    this.messageService.add({ severity: 'error', summary: 'Unable to update partner\'s limits.', detail: new ResponseModel(response.error).message });
                }
            );
    }

    public onRowDeleteClicked(group: ServiceLevelConfigurationGroup): void {
        this.confirmationService.confirm({
            message: 'Do you want to delete \'' + group.serviceLevelName + '\'?',
            header: 'Confirmation',
            icon: 'pi pi-info-circle',
            acceptLabel: 'Ok',
            acceptVisible: true,
            rejectLabel: 'Cancel',
            rejectVisible: true,
            accept: /* istanbul ignore next */ () => this.deleteServiceLevelConfigurations(group)
        });
    }

    private deleteServiceLevelConfigurations(group: ServiceLevelConfigurationGroup): void {
        this.progressIndicatingService.show({
            progress$: this.progress$.asObservable(),
            initial: {
                header: 'Deleting Partner\'s Configurations',
                details: 'Please wait...',
                done: false
            }
        });
        this.topconConfigurationService.deleteServiceLevelConfigurationByServiceLevel(group.serviceLevelID)
            .pipe(
                map(responseDto => new ResponseModel(responseDto)),
                tap(() => {
                    this.messageService.add({ severity: 'success', summary: 'Success', detail: '\'' + group.serviceLevelName + '\' configurations deleted successfully.' });
                }),
                progressTap(this.progress$, {
                    done: false,
                    header: 'Loading Configuration Option Details',
                    details: 'Please wait...'
                }),
                this.reloadConfigurationOptionsMap(),
                this.reloadPartnerMap(),
                concatMap(response => iif(() => response.serviceLevelConfiguration === undefined,
                    throwError({ error: { responseMessage: 'Can\'t find configuration for this partner ' + this.selectedPartner.partnerId } }),
                    of(new TopconConfigurationResponseModel(response)))
                ),
                takeUntil(this.unsubscribe$))
            .subscribe(
                (response: TopconConfigurationResponseModel) => {
                    this.sendProgressDoneSignal();
                    this.handleSelectedPartnerIdChangedResponse(response);
                },
                () => {
                    this.sendProgressDoneSignal();
                    this.messageService.add({ severity: 'error', summary: 'Problems', detail: 'Unable to delete ' + group.serviceLevelName + '\' configurations.' });
                });
    }

    private clonedTestActivationConfig: {[s: string]: TestActivationConfig} = {};
    public currentEditingTestActivationConfig: number;
    public onTestActivationRowEditInit(config: TestActivationConfig): void {
        this.originalData = {};
        this.clonedTestActivationConfig[config.testActivationConfigId] = { ...config };
        this.originalData = JSON.parse(JSON.stringify(config));
        this.currentEditingTestActivationConfig = config.testActivationConfigId;
    }

    public onTestActivationRowEditSave(config: TestActivationConfig): void {
        this.currentEditingTestActivationConfig = -1;
        config.modelStatus = ModelStatus.UPDATE;
        config.productId = 0;
        this.progressIndicatingService.show({
            progress$: this.progress$.asObservable(),
            initial: {
                header: 'Saving Test Activation Mapping',
                details: 'Please wait...',
                done: false
            }
        });
        this.topconConfigurationService.updateTestActivationConfig(config)
            .pipe(
                takeUntil(this.unsubscribe$))
            .subscribe(
                () => {
                    delete this.clonedTestActivationConfig[config.testActivationConfigId];
                    this.sendProgressDoneSignal();
                    this.onSelectedPartnerIdChanged();
                },
                () => {
                    this.sendProgressDoneSignal();
                    this.messageService.add({ severity: 'error', summary: 'fail', detail: 'Unable to save the configuration.' });
                    config.productCode = this.originalData.productCode;
                    config.accessCodeName = this.originalData.accessCodeName;
                    if (this.originalData.accessCodeId !== config.accessCodeId) {
                        config.accessCodeId = this.originalData.accessCodeId;
                    }
                });
    }

    public onTestActivationRowEditCancel(config: TestActivationConfig, index: number): void {
        this.testActivationConfigs[index] = this.clonedTestActivationConfig[config.testActivationConfigId];
        delete this.clonedTestActivationConfig[config.testActivationConfigId];
        this.currentEditingTestActivationConfig = -1;
    }

    public onTestActivationRowDeleteClicked(config: TestActivationConfig): void {
        this.confirmationService.confirm({
            message: `Do you want to delete  ${config.accessCodeName} - ${config.productCode}?`,
            header: 'Confirmation',
            icon: 'pi pi-info-circle',
            acceptLabel: 'Ok',
            acceptVisible: true,
            rejectLabel: 'Cancel',
            rejectVisible: true,
            accept: /* istanbul ignore next */ () => this.deleteTestActivationConfig(config)
        });
    }

    private deleteTestActivationConfig(config: TestActivationConfig): void {
        this.progressIndicatingService.show({
            progress$: this.progress$.asObservable(),
            initial: {
                header: 'Deleting Test Activation Mapping',
                details: 'Please wait...',
                done: false
            }
        });
        this.topconConfigurationService.deleteTestActivationConfig(config.testActivationConfigId)
            .pipe(
                takeUntil(this.unsubscribe$))
            .subscribe(
                () => {
                    delete this.clonedTestActivationConfig[config.testActivationConfigId];
                    this.sendProgressDoneSignal();
                    this.onSelectedPartnerIdChanged();
                },
                () => {
                    this.sendProgressDoneSignal();
                    this.messageService.add({ severity: 'error', summary: 'Problems', detail: 'Unable to delete the configuration.' });
                });
    }
}
